<template>
  <HrPageLayout
    title="Super Admin"
    subtitle="Onboard companies and create company admins (multi-tenant control)"
    icon="⚙️"
    :breadcrumb-items="[{ label: 'Super Admin' }, { label: 'Companies' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreateModal">
        <i class="bx bx-plus"></i> Add company
      </button>
    </template>

    <div class="hr-chart-card">
      <div class="hr-card-body">
        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading companies...</p>
        </div>
        <div v-else-if="!companies.length" class="hr-empty-state hr-py-5">
          <i class="bx bx-building hr-empty-icon"></i>
          <p>No companies yet. Add a company to onboard a new tenant.</p>
        </div>
        <div v-else class="hr-table-wrapper">
          <table class="hr-table hr-table-bordered hr-table-hover">
            <thead>
              <tr>
                <th>#</th>
                <th>Company name</th>
                <th>Code</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Created</th>
                <th width="260">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in companies" :key="c.id">
                <td>{{ i + 1 }}</td>
                <td>{{ c.name }}</td>
                <td><code>{{ c.code }}</code></td>
                <td><span v-if="c.contact_person || c.email">{{ c.contact_person || '—' }}<template v-if="c.email"> · {{ c.email }}</template></span><span v-else>—</span></td>
                <td><span :class="['hr-badge', c.status === 'Active' ? 'hr-badge-success' : 'hr-badge-secondary']">{{ c.status }}</span></td>
                <td>{{ formatDate(c.created_at) }}</td>
                <td>
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-secondary hr-mr-1" @click="openAdminsModal(c)" title="View and manage admins">
                    <i class="bx bx-group"></i> Admins
                  </button>
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openCreateAdminModal(c)" title="Create admin for this company">
                    <i class="bx bx-user-plus"></i> Create admin
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal: Add company -->
    <div v-if="showCreateModal" class="hr-modal-overlay" @click.self="closeCreateModal">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Onboard company</h5>
            <button type="button" class="hr-modal-close" @click="closeCreateModal" aria-label="Close"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <div class="hr-form-grid hr-form-grid-2">
              <div class="hr-field-full">
                <label class="hr-form-label">Company name <span class="hr-text-danger">*</span></label>
                <input v-model="createForm.name" type="text" class="hr-input" placeholder="e.g. Acme Ltd" />
              </div>
              <div>
                <label class="hr-form-label">Company code <span class="hr-text-danger">*</span></label>
                <input v-model="createForm.code" type="text" class="hr-input" placeholder="e.g. acme (alphanumeric)" />
                <small class="hr-form-hint">Unique short code; letters, numbers, underscore, hyphen only.</small>
              </div>
              <div>
                <label class="hr-form-label">Email</label>
                <input v-model="createForm.email" type="email" class="hr-input" placeholder="info@company.com" />
              </div>
              <div>
                <label class="hr-form-label">Phone</label>
                <input v-model="createForm.phone" type="text" class="hr-input" placeholder="+254..." />
              </div>
              <div class="hr-field-full">
                <label class="hr-form-label">Address</label>
                <input v-model="createForm.address" type="text" class="hr-input" placeholder="Physical address or P.O. Box" />
              </div>
              <div>
                <label class="hr-form-label">Contact person</label>
                <input v-model="createForm.contact_person" type="text" class="hr-input" placeholder="Name of main contact" />
              </div>
              <div>
                <label class="hr-form-label">Website</label>
                <input v-model="createForm.website" type="text" class="hr-input" placeholder="https://..." />
              </div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeCreateModal">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" @click="createCompany" :disabled="saving">
              {{ saving ? 'Creating...' : 'Create company' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Create company admin -->
    <div v-if="showAdminModal" class="hr-modal-overlay" @click.self="closeAdminModal">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Create admin for {{ adminForm.companyName }}</h5>
            <button type="button" class="hr-modal-close" @click="closeAdminModal" aria-label="Close"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <p class="hr-text-muted hr-mb-4">This user will have full admin rights for this company only. They can log in with email or ID No.</p>
            <div class="hr-form-grid hr-form-grid-2">
              <div>
                <label class="hr-form-label">Admin name <span class="hr-text-danger">*</span></label>
                <input v-model="adminForm.admin_name" type="text" class="hr-input" placeholder="Full name" />
              </div>
              <div>
                <label class="hr-form-label">ID No / Staff number (login) <span class="hr-form-hint-inline">optional</span></label>
                <input v-model="adminForm.admin_id_no" type="text" class="hr-input" placeholder="e.g. 12345678 or EMP001" />
                <small class="hr-form-hint">Used for login with ID. If left blank, they will log in with email only.</small>
              </div>
              <div>
                <label class="hr-form-label">Admin email (login) <span class="hr-text-danger">*</span></label>
                <input v-model="adminForm.admin_email" type="email" class="hr-input" placeholder="admin@company.com" />
              </div>
              <div>
                <label class="hr-form-label">Phone</label>
                <input v-model="adminForm.admin_phone" type="text" class="hr-input" placeholder="+254..." />
              </div>
              <div class="hr-field-full">
                <label class="hr-form-label">Password <span class="hr-text-danger">*</span></label>
                <input v-model="adminForm.admin_password" type="password" class="hr-input" placeholder="Min 8 characters" autocomplete="new-password" />
              </div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeAdminModal">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" @click="createAdmin" :disabled="adminSaving">
              {{ adminSaving ? 'Creating...' : 'Create admin' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: List company admins -->
    <div v-if="showAdminsModal" class="hr-modal-overlay" @click.self="closeAdminsModal">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Admins – {{ adminsModalCompany.name }}</h5>
            <button type="button" class="hr-modal-close" @click="closeAdminsModal" aria-label="Close"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <div v-if="adminsLoading" class="hr-text-center hr-py-4">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted hr-mt-2 mb-0">Loading admins...</p>
            </div>
            <div v-else-if="!companyAdmins.length" class="hr-text-muted hr-text-center hr-py-4">
              No admins for this company yet. Use “Create admin” to add one.
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-bordered hr-table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>ID No</th>
                    <th>Status</th>
                    <th width="160">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in companyAdmins" :key="a.userID">
                    <td>{{ a.names }}</td>
                    <td>{{ a.email }}</td>
                    <td>{{ a.idNo || '—' }}</td>
                    <td><span :class="['hr-badge', a.status === 'active' ? 'hr-badge-success' : 'hr-badge-secondary']">{{ a.status }}</span></td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary hr-mr-1" @click="openEditAdminModal(a)" :disabled="a.status !== 'active'" title="Edit">Edit</button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="confirmDeactivateAdmin(a)" :disabled="a.status !== 'active'" title="Deactivate">Deactivate</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeAdminsModal">Close</button>
            <button type="button" class="hr-btn hr-btn-primary" @click="openCreateAdminFromAdminsModal">
              <i class="bx bx-user-plus"></i> Create admin
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Edit company admin -->
    <div v-if="showEditAdminModal" class="hr-modal-overlay" @click.self="closeEditAdminModal">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Edit admin</h5>
            <button type="button" class="hr-modal-close" @click="closeEditAdminModal" aria-label="Close"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <div class="hr-form-grid hr-form-grid-2">
              <div>
                <label class="hr-form-label">Admin name <span class="hr-text-danger">*</span></label>
                <input v-model="editForm.admin_name" type="text" class="hr-input" placeholder="Full name" />
              </div>
              <div>
                <label class="hr-form-label">ID No / Staff number (login)</label>
                <input v-model="editForm.admin_id_no" type="text" class="hr-input" placeholder="e.g. 12345678" />
              </div>
              <div>
                <label class="hr-form-label">Admin email (login) <span class="hr-text-danger">*</span></label>
                <input v-model="editForm.admin_email" type="email" class="hr-input" placeholder="admin@company.com" />
              </div>
              <div>
                <label class="hr-form-label">Phone</label>
                <input v-model="editForm.admin_phone" type="text" class="hr-input" placeholder="+254..." />
              </div>
              <div class="hr-field-full">
                <label class="hr-form-label">New password <span class="hr-form-hint-inline">optional</span></label>
                <input v-model="editForm.admin_password" type="password" class="hr-input" placeholder="Leave blank to keep current" autocomplete="new-password" />
              </div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeEditAdminModal">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" @click="updateAdmin" :disabled="editSaving">
              {{ editSaving ? 'Saving...' : 'Save changes' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm deactivate -->
    <div v-if="showDeactivateConfirm" class="hr-modal-overlay" @click.self="showDeactivateConfirm = false">
      <div class="hr-modal-dialog">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5 class="hr-modal-title">Deactivate admin</h5>
            <button type="button" class="hr-modal-close" @click="showDeactivateConfirm = false" aria-label="Close"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <p>Deactivate <strong>{{ deactivateTarget.names }}</strong> ({{ deactivateTarget.email }})? They will no longer be able to log in.</p>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showDeactivateConfirm = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-danger" @click="deactivateAdmin" :disabled="deactivateSaving">
              {{ deactivateSaving ? 'Deactivating...' : 'Deactivate' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "SuperAdminDashboard",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      adminSaving: false,
      editSaving: false,
      deactivateSaving: false,
      companies: [],
      showCreateModal: false,
      showAdminModal: false,
      showAdminsModal: false,
      showEditAdminModal: false,
      showDeactivateConfirm: false,
      adminsModalCompany: { id: null, name: "" },
      companyAdmins: [],
      adminsLoading: false,
      deactivateTarget: { userID: null, names: "", email: "" },
      createForm: {
        name: "",
        code: "",
        email: "",
        phone: "",
        address: "",
        contact_person: "",
        website: ""
      },
      adminForm: {
        company_id: null,
        companyName: "",
        admin_name: "",
        admin_id_no: "",
        admin_email: "",
        admin_phone: "",
        admin_password: ""
      },
      editForm: {
        userID: null,
        admin_name: "",
        admin_id_no: "",
        admin_email: "",
        admin_phone: "",
        admin_password: ""
      }
    };
  },
  mounted() {
    this.loadCompanies();
  },
  methods: {
    async loadCompanies() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getCompanies");
        const res = await execute(data, authUrl);
        if (res?.data?.success && Array.isArray(res.data.data)) {
          this.companies = res.data.data;
        } else {
          this.companies = [];
        }
      } catch (e) {
        this.companies = [];
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.createForm = { name: "", code: "" };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async createCompany() {
      const name = (this.createForm.name || "").trim();
      const code = (this.createForm.code || "").trim().replace(/[^a-zA-Z0-9_-]/g, "");
      if (!name) {
        this.$toast?.error?.("Company name is required") || alert("Company name is required");
        return;
      }
      if (!code) {
        this.$toast?.error?.("Company code is required (alphanumeric)") || alert("Company code is required");
        return;
      }
      this.saving = true;
      try {
        const data = new FormData();
        data.append("function", "createCompany");
        data.append("name", name);
        data.append("code", code);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success?.("Company created") || alert("Company created.");
          this.closeCreateModal();
          this.loadCompanies();
        } else {
          this.$toast?.error?.(res?.data?.message || "Failed") || alert(res?.data?.message || "Failed");
        }
      } catch (e) {
        this.$toast?.error?.("Request failed") || alert("Request failed");
      } finally {
        this.saving = false;
      }
    },
    openCreateAdminModal(company) {
      this.adminForm = {
        company_id: company.id,
        companyName: company.name,
        admin_name: "",
        admin_id_no: "",
        admin_email: "",
        admin_phone: "",
        admin_password: ""
      };
      this.showAdminModal = true;
    },
    closeAdminModal() {
      this.showAdminModal = false;
    },
    async openAdminsModal(company) {
      this.adminsModalCompany = { id: company.id, name: company.name };
      this.companyAdmins = [];
      this.showAdminsModal = true;
      this.loadCompanyAdmins();
    },
    closeAdminsModal() {
      this.showAdminsModal = false;
      this.showEditAdminModal = false;
      this.showDeactivateConfirm = false;
    },
    async loadCompanyAdmins() {
      if (!this.adminsModalCompany.id) return;
      this.adminsLoading = true;
      try {
        const data = new FormData();
        data.append("function", "getCompanyAdmins");
        data.append("company_id", this.adminsModalCompany.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success && Array.isArray(res.data.data)) {
          this.companyAdmins = res.data.data;
        } else {
          this.companyAdmins = [];
        }
      } catch (e) {
        this.companyAdmins = [];
      } finally {
        this.adminsLoading = false;
      }
    },
    openCreateAdminFromAdminsModal() {
      const company = this.companies.find(c => c.id === this.adminsModalCompany.id) || { id: this.adminsModalCompany.id, name: this.adminsModalCompany.name };
      this.showAdminsModal = false;
      this.$nextTick(() => this.openCreateAdminModal(company));
    },
    openEditAdminModal(admin) {
      this.editForm = {
        userID: admin.userID,
        admin_name: admin.names || "",
        admin_id_no: admin.idNo || "",
        admin_email: admin.email || "",
        admin_phone: admin.phoneNumber || "",
        admin_password: ""
      };
      this.showEditAdminModal = true;
    },
    closeEditAdminModal() {
      this.showEditAdminModal = false;
    },
    async updateAdmin() {
      const { userID, admin_name, admin_email } = this.editForm;
      if (!userID || !(admin_name || "").trim() || !(admin_email || "").trim()) {
        this.$toast?.error?.("Name and email are required") || alert("Name and email are required");
        return;
      }
      this.editSaving = true;
      try {
        const data = new FormData();
        data.append("function", "updateCompanyAdmin");
        data.append("userID", userID);
        data.append("admin_name", (admin_name || "").trim());
        data.append("admin_email", (admin_email || "").trim());
        if ((this.editForm.admin_id_no || "").trim()) data.append("admin_id_no", (this.editForm.admin_id_no || "").trim());
        if ((this.editForm.admin_phone || "").trim()) data.append("admin_phone", (this.editForm.admin_phone || "").trim());
        if ((this.editForm.admin_password || "").trim()) data.append("admin_password", this.editForm.admin_password);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success?.("Admin updated.") || alert("Admin updated.");
          this.closeEditAdminModal();
          this.loadCompanyAdmins();
        } else {
          this.$toast?.error?.(res?.data?.message || "Failed") || alert(res?.data?.message || "Failed");
        }
      } catch (e) {
        this.$toast?.error?.("Request failed") || alert("Request failed");
      } finally {
        this.editSaving = false;
      }
    },
    confirmDeactivateAdmin(admin) {
      this.deactivateTarget = { userID: admin.userID, names: admin.names, email: admin.email };
      this.showDeactivateConfirm = true;
    },
    async deactivateAdmin() {
      const { userID } = this.deactivateTarget;
      if (!userID) return;
      this.deactivateSaving = true;
      try {
        const data = new FormData();
        data.append("function", "deleteCompanyAdmin");
        data.append("userID", userID);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success?.("Admin deactivated.") || alert("Admin deactivated.");
          this.showDeactivateConfirm = false;
          this.loadCompanyAdmins();
        } else {
          this.$toast?.error?.(res?.data?.message || "Failed") || alert(res?.data?.message || "Failed");
        }
      } catch (e) {
        this.$toast?.error?.("Request failed") || alert("Request failed");
      } finally {
        this.deactivateSaving = false;
      }
    },
    async createAdmin() {
      const { company_id, admin_name, admin_email, admin_password } = this.adminForm;
      if (!company_id || !(admin_name || "").trim() || !(admin_email || "").trim() || !admin_password) {
        this.$toast?.error?.("Name, email and password are required") || alert("Name, email and password are required");
        return;
      }
      this.adminSaving = true;
      try {
        const data = new FormData();
        data.append("function", "createCompanyAdmin");
        data.append("company_id", company_id);
        data.append("admin_name", (admin_name || "").trim());
        data.append("admin_email", (admin_email || "").trim());
        data.append("admin_password", admin_password);
        if ((this.adminForm.admin_id_no || "").trim()) data.append("admin_id_no", (this.adminForm.admin_id_no || "").trim());
        if ((this.adminForm.admin_phone || "").trim()) data.append("admin_phone", (this.adminForm.admin_phone || "").trim());
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success?.("Admin created. They can log in with the provided email and password.") || alert("Admin created.");
          this.closeAdminModal();
        } else {
          this.$toast?.error?.(res?.data?.message || "Failed") || alert(res?.data?.message || "Failed");
        }
      } catch (e) {
        this.$toast?.error?.("Request failed") || alert("Request failed");
      } finally {
        this.adminSaving = false;
      }
    },
    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    }
  }
};
</script>

<style scoped>
.hr-form-label { display: block; font-weight: 600; margin-bottom: 0.35rem; font-size: 0.9rem; }
.hr-form-hint { font-size: 0.8rem; color: #64748b; margin-top: 0.25rem; }
.hr-form-hint-inline { font-weight: normal; color: #64748b; font-size: 0.85em; }
.hr-text-danger { color: #dc2626; }
.hr-empty-icon { font-size: 48px; color: #94a3b8; }
.hr-form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.hr-field-full { grid-column: 1 / -1; }
@media (max-width: 640px) { .hr-form-grid-2 { grid-template-columns: 1fr; } }
code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
.hr-mr-1 { margin-right: 0.25rem; }
.hr-btn-outline-danger { border: 1px solid #dc2626; color: #dc2626; background: transparent; }
.hr-btn-outline-danger:hover:not(:disabled) { background: #fef2f2; }
.hr-btn-danger { background: #dc2626; color: #fff; border: none; }
.hr-btn-danger:hover:not(:disabled) { background: #b91c1c; }
</style>
